import { inject, Injectable } from '@angular/core';
import { catchError, map, of, take } from 'rxjs';
import { NotificationService } from './notification.service';
import { SessionService } from './session.service';
import { SessionStore } from '../app-state/session.store';
import { CustomizationStore } from '../app-state/customization.store';
import { isNotificationPopupAllowed } from '../utils/notification-popup-scope';
import { environment } from '../../environments/environment';

export type WebsocketPopupType = 'info' | 'warning' | 'error' | 'success';

/** Websocket pop-ups close on their own; the user didn't ask for them. */
export const WEBSOCKET_POPUP_DURATION_MS = 10_000;

export interface WebsocketPopupRequest {
  type: WebsocketPopupType;
  title: string;
  message: string;
  key?: string;
  /** The event's case (session) id; omit only for genuinely case-less events. */
  sessionId?: string;
  sessionName?: string;
  showSessionName?: boolean;
}

/**
 * The single gate for notification pop-ups raised by websocket events: it
 * applies the user's notification pop-up scope against the active case in
 * this tab. `notify()` is the entry point for pop-ups raised in
 * `WebSocketService`; a component that raises one itself from a websocket
 * emitter must check `isPopupAllowed()` first. Pop-ups from the user's own
 * actions must not come through here; they use `NotificationService`
 * directly and are never gated.
 */
@Injectable({ providedIn: 'root' })
export class WebsocketPopupService {
  private readonly notificationService = inject(NotificationService);
  private readonly sessionService = inject(SessionService);
  private readonly sessionStore = inject(SessionStore);
  private readonly customizationStore = inject(CustomizationStore);
  /** Best-effort cache of case names shown in notification pop-ups. */
  private readonly caseNameCache = new Map<string, string>();

  /** Whether a pop-up about `eventSessionId` may be shown in this tab right now. */
  isPopupAllowed(eventSessionId: string | undefined): boolean {
    return isNotificationPopupAllowed(
      this.customizationStore.notificationPopupScope(),
      this.sessionStore.activeSession()?.id,
      eventSessionId,
    );
  }

  /**
   * Show a websocket-driven pop-up with case context when allowed.
   * Lazy-loads the case name if needed. Silently skips on lookup failure
   * (user not authorized / case gone) instead of showing "Unknown case".
   */
  notify(request: WebsocketPopupRequest): void {
    const { sessionId } = request;
    // Checked before the lookup: getSession() can itself raise interceptor
    // toasts ("Access Revoked", "Case Removed") for a case we'd never show.
    if (!this.isPopupAllowed(sessionId)) return;

    if (!sessionId) {
      if (!environment.production) {
        console.warn('Websocket pop-up without a session id:', request.title);
      }
      this.show(request, request.message);
      return;
    }

    const syncName = this.resolveCaseNameSync(sessionId);
    if (syncName) {
      this.show(request, this.formatCaseMessage(request, syncName));
      return;
    }

    this.sessionService
      .getSession(sessionId)
      .pipe(
        map((session) => session?.value?.trim() || null),
        catchError(() => of(null)),
        take(1),
      )
      .subscribe((caseName) => {
        if (!caseName) return;
        this.caseNameCache.set(sessionId, caseName);
        // The user may have changed case while the lookup was in flight.
        if (!this.isPopupAllowed(sessionId)) return;
        this.show(request, this.formatCaseMessage(request, caseName));
      });
  }

  private show(request: WebsocketPopupRequest, content: string): void {
    this.notificationService.create(
      request.type,
      request.title,
      content,
      request.key,
      { nzDuration: WEBSOCKET_POPUP_DURATION_MS },
    );
  }

  /**
   * Resolve a case display name for a notification pop-up.
   * Order: cache -> activeSession -> current paginated sessions page.
   * Returns undefined when not available locally.
   */
  private resolveCaseNameSync(sessionId: string): string | undefined {
    const cached = this.caseNameCache.get(sessionId);
    if (cached) return cached;

    const activeSession = this.sessionStore.activeSession();
    if (activeSession?.id === sessionId && activeSession.value) {
      this.caseNameCache.set(sessionId, activeSession.value);
      return activeSession.value;
    }

    const pageSession = this.sessionStore
      .sessions()
      .find((session) => session.id === sessionId);
    if (pageSession?.value) {
      this.caseNameCache.set(sessionId, pageSession.value);
      return pageSession.value;
    }

    return undefined;
  }

  private formatCaseMessage(
    request: WebsocketPopupRequest,
    caseName: string,
  ): string {
    // The content is rendered as HTML, so names (user data) are escaped.
    const caseLine = `Case: <b>${escapeHtml(caseName)}</b>`;
    const normalizedSessionName = request.sessionName?.trim();
    if (normalizedSessionName && request.showSessionName) {
      return `${request.message}\n${caseLine}<br>Session: ${escapeHtml(normalizedSessionName)}`;
    }
    return `${request.message}\n${caseLine}`;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
